import { useCallback } from "react";

import Button from "@mui/material/Button";
import Link from "@mui/material/Link";
import ListItemText from "@mui/material/ListItemText";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import { useAppDispatch } from "app/hooks";
import {
  setCustomMessage,
  setOpen as setSnackbarOpen
} from "components/action-snackbar/action-snackbar-slice";
import { buildMentionChartNoteHref } from "components/mentions/mentions.util";
import TeamPill from "components/team-pill";
import { CHART_NOTE_TYPES } from "constants/chart-notes";
import {
  ALREADY_TAKEN_MESSAGE,
  NOT_A_MEMBER_MESSAGE
} from "features/staff-tasks-v2/components/staff-task-modal-dialog/view-staff-task-form/team-task-banner.component";
import useStaffTasksV2 from "features/staff-tasks-v2/hooks/use-staff-tasks-v2.hook";
import useCanViewVisitNotes from "hooks/use-can-view-visit-notes.hook";
import { useCreateChartNoteMentionTaskMutation } from "services/chart-notes";
import {
  type ChartNoteMentionNotificationExtra,
  type MentionNotification,
  type TaskMentionNotificationExtra,
  isTaskMentionExtra
} from "services/types/notifications";
import { isVisitNoteHidden } from "utils/visit-note-visibility.util";

const snippetSx = {
  borderLeft: "3px solid",
  borderColor: "divider",
  pl: 1,
  whiteSpace: "pre-line"
} as const;

/** The team a mention row reached this person through (TEAMS_FEATURE), as a pill. */
const MentionTeamPill = ({
  extra
}: {
  extra: { team_id?: number; team_name?: string; team_color?: string };
}) =>
  extra.team_id && extra.team_name ? (
    <TeamPill
      name={extra.team_name}
      color={extra.team_color}
      testId="mention-notification-team"
    />
  ) : null;

/**
 * "Jackie Cooper mentioned you in a chart note"
 *  Margaret Ellis · General Note
 *  | …order #PH-448120. ETA Oct 14. FYI @Karen Ruiz
 *  Open chart note   Make it a task
 *
 * For a team mention ("… mentioned Chicago North PCCs in a chart note", TEAMS_FEATURE) the task
 * action is "Take it": the first member to click gets the task, sent to the team and already theirs.
 */
const ChartNoteMention = ({
  title,
  extra
}: {
  title: string;
  extra: ChartNoteMentionNotificationExtra;
}) => {
  const dispatch = useAppDispatch();
  const { handleOpenTaskModal } = useStaffTasksV2();
  const [createTask, { isLoading }] = useCreateChartNoteMentionTaskMutation();
  const { patient_id, chart_note_id, patient_name, chart_note_type, snippet } =
    extra;

  const noteTypeLabel = CHART_NOTE_TYPES.getFromCode(chart_note_type);
  // ENG-2484: a mention in a visit note the user may not see shows no snippet and offers no task,
  // since the task's description is a copy of the note's text. "Open chart note" stays: it lands
  // on the hidden-note placeholder.
  const canViewVisitNotes = useCanViewVisitNotes();
  const isNoteHidden = isVisitNoteHidden(
    { type: chart_note_type },
    canViewVisitNotes
  );

  const isTeamMention = Boolean(extra.team_id);
  const handleMakeTask = useCallback(
    // The click still reaches the row, which marks the notification read, as "Open task" does.
    async () => {
      try {
        const { task_id } = await createTask(chart_note_id).unwrap();
        handleOpenTaskModal({ mode: "view", taskId: task_id });
      } catch (error) {
        const status =
          typeof error === "object" && error && "status" in error
            ? Number((error as { status: unknown }).status)
            : undefined;
        dispatch(
          setCustomMessage(
            !isTeamMention
              ? "Could not create the task. The chart note may have been deleted."
              : status === 400
                ? ALREADY_TAKEN_MESSAGE
                : status === 403
                  ? NOT_A_MEMBER_MESSAGE
                  : "Could not take it. The chart note may have been deleted."
          )
        );
        dispatch(setSnackbarOpen(true));
      }
    },
    [chart_note_id, createTask, dispatch, handleOpenTaskModal, isTeamMention]
  );
  const taskActionLabel = extra.task_id
    ? "Open task"
    : isTeamMention
      ? "Take it"
      : "Make it a task";

  return (
    <ListItemText
      primary={title}
      secondary={
        <Stack component="span" gap={0.5}>
          <MentionTeamPill extra={extra} />
          <Typography component="span" variant="body2" color="text.secondary">
            {[patient_name, noteTypeLabel].filter(Boolean).join(" · ")}
          </Typography>
          {snippet && !isNoteHidden && (
            <Typography component="span" variant="body2" sx={snippetSx}>
              {snippet}
            </Typography>
          )}
          {isNoteHidden && (
            <Typography component="span" variant="body2" color="text.secondary">
              Visit note hidden: only audiologists and business owners can view
              visit notes.
            </Typography>
          )}
          <Stack component="span" direction="row" gap={2} alignItems="center">
            {patient_id && chart_note_id && (
              <Link
                href={buildMentionChartNoteHref(patient_id, chart_note_id)}
                underline="hover"
              >
                Open chart note
              </Link>
            )}
            {(!isNoteHidden || extra.task_id) && (
              <Button
                size="small"
                variant="text"
                onClick={handleMakeTask}
                disabled={isLoading}
                sx={{ p: 0, minWidth: 0 }}
              >
                {taskActionLabel}
              </Button>
            )}
          </Stack>
        </Stack>
      }
    />
  );
};

/**
 * "Jackie Cooper mentioned you in a task" (or "in a task comment")
 *  Margaret Ellis · Call about the repair
 *  | …can you call her back? @Karen Ruiz
 *  Open task
 */
const TaskMention = ({
  title,
  extra
}: {
  title: string;
  extra: TaskMentionNotificationExtra;
}) => {
  const { handleOpenTaskModal } = useStaffTasksV2();
  const { patient_name, staff_task_title, staff_task_id, snippet } = extra;

  return (
    <ListItemText
      primary={title}
      secondary={
        <Stack component="span" gap={0.5}>
          <MentionTeamPill extra={extra} />
          <Typography component="span" variant="body2" color="text.secondary">
            {[patient_name, staff_task_title].filter(Boolean).join(" · ")}
          </Typography>
          {snippet && (
            <Typography component="span" variant="body2" sx={snippetSx}>
              {snippet}
            </Typography>
          )}
          <Stack component="span" direction="row" gap={2} alignItems="center">
            {/* The row's own click still marks the notification read. */}
            <Button
              size="small"
              variant="text"
              onClick={() =>
                handleOpenTaskModal({ mode: "view", taskId: staff_task_id })
              }
              sx={{ p: 0, minWidth: 0 }}
            >
              Open task
            </Button>
          </Stack>
        </Stack>
      }
    />
  );
};

/** A bell row for an @mention: chart note, task description or task comment (`extra.kind`). */
const MentionNotificationComponent = ({ title, extra }: MentionNotification) =>
  isTaskMentionExtra(extra) ? (
    <TaskMention title={title} extra={extra} />
  ) : (
    <ChartNoteMention title={title} extra={extra} />
  );

export default MentionNotificationComponent;
